import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./game.css";
import { fireGameAlert } from "./gameAlert";
import { ReactComponent as RoomMap } from "./img/room/room.svg";
import { ReactComponent as RoomMapMobile } from "./img/room/room_mob.svg";
import jewelIconImg from "./img/room/jewel-icon.png";
import mobileImg from "./img/room/mobile.png";
import jisImg from "./img/room/jis.png";
import keyImg from "./img/room/key.png";
import bulbImg from "./img/room/bulb.png";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markGameOver,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 1;
const audioClip = "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/customer_centricity/new.mp3";
const ROOM_DURATION_SEC = 20 * 60; // old/room.php's fixed 20-minute countdown

// jis.png's real pixel size (2408x1250) — the jigsaw grid uses this instead
// of forcing a square, so tiles aren't cropped/stretched off their source art.
const JIS_IMAGE_ASPECT_RATIO = "2408 / 1250";

// Clickable props in room.svg / room_mob.svg. The desktop export tags them
// with classes (.exit/.cls-1/.lasttab); the mobile export only has ids
// (EXIT, _555, Vault), so match either.
const ROOM_HOTSPOTS = {
  exit: ".exit, #EXIT, #EXit_Door",
  locker: ".cls-1, #_555",
  vault: ".lasttab, #Vault",
  trolley: "#Trolley",
};

// old/room.php's "cryptic gibberish" locker code (myModal).
const GIBBERISH_QUESTIONS = [
  {
    id: "a1",
    label: "Ha ow kwic ly rr v aebell too rez olv kus tummer ishoe iz colled",
    hint: "ffeeiiccny",
    answer: "efficiency",
  },
  {
    id: "a2",
    label: "Ha ow curr ectlee v rez olv kus tummer ishoe iz colled",
    hint: "nessfftiveece",
    answer: "effectiveness",
  },
  {
    id: "a3",
    label: "May king a ko neck tion wid kus tummer iz colled",
    hint: "mephtya",
    answer: "empathy",
  },
];

// old/room.php's final 3-letter code that unlocks the left locker (lastModal).
const FINAL_CODE_QUESTIONS = [
  {
    id: "code",
    label:
      "That's awesome! You have made the customers experience simple. Insert your 3 letter code to proceed",
    answer: "eee",
  },
];

// old/room.php's trolley/mobile-phone questions (modalCode).
const TROLLEY_QUESTIONS = [
  {
    id: "a1",
    label: "By doing what's right and following through, we must act",
    hint: "blyresnopis",
    answer: "responsibly",
  },
  {
    id: "a2",
    label: "By being clear and transparent, we must always try to keep it",
    hint: "lepmis",
    answer: "simple",
  },
  {
    id: "a3",
    label: "By engaging with all people and possibilities, we must be",
    hint: "peon",
    answer: "open",
  },
];

// old/room.php's final audio-clue key (audioModal).
const AUDIO_QUESTIONS = [
  {
    id: "key",
    label: "Insert KEY to provide the experience:",
    hint: "mercusto citycenirt",
    answer: "customer centricity",
  },
];

const MOBILE_PORTRAIT_QUERY = "(max-width: 768px) and (orientation: portrait)";

function useIsMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(
    () => window.matchMedia(MOBILE_PORTRAIT_QUERY).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_PORTRAIT_QUERY);
    const onChange = (e) => setIsMobilePortrait(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isMobilePortrait;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

/** Shuffle guaranteeing a full derangement — no tile starts in its correct
 *  spot (old/room.php's puzzle.js never shows any square pre-solved either). */
function shuffleOrder(size) {
  const arr = Array.from({ length: size }, (_, i) => i);
  if (size <= 1) return arr;
  const hasFixedPoint = (a) => a.some((v, i) => v === i);
  do {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  } while (hasFixedPoint(arr));
  return arr;
}

/** old/room.php's #puzzle1 jigsaw, reimplemented with Pointer Events driving
 *  a hand-rolled drag (mouse + touch, one code path): the native HTML5 Drag
 *  and Drop API's browser-generated ghost image turned out unreliable here
 *  (no visible "held" piece while dragging, just an instant swap), so this
 *  renders its own floating piece that tracks the pointer every frame,
 *  giving full control over the visual. A plain tap (no movement past the
 *  threshold) still falls through to the original tap-two-tiles-to-swap
 *  click handling. */
function JigsawPuzzle({ image, size = 3, aspectRatio = "1 / 1", startSolved = false, onSolved }) {
  const total = size * size;
  // Reopened from the hint bulb after it's been solved: show the finished
  // picture instead of reshuffling (and don't fire onSolved again).
  const [order, setOrder] = useState(() =>
    startSolved ? Array.from({ length: total }, (_, i) => i) : shuffleOrder(total)
  );
  const [selected, setSelected] = useState(null);
  const [dragOverPos, setDragOverPos] = useState(null);
  const [solved, setSolved] = useState(startSolved);
  const [draggingPos, setDraggingPos] = useState(null);
  // Floating ghost's live position/size while a drag is in progress.
  const [ghost, setGhost] = useState(null);
  const dragStateRef = useRef(null); // { pos, startX, startY, width, height, moved }
  const suppressClickRef = useRef(false); // swallow the click a real drag leaves behind

  useEffect(() => {
    if (solved) return;
    if (order.every((v, i) => v === i)) {
      setSolved(true);
      onSolved?.();
    }
  }, [order, solved, onSolved]);

  const swapTiles = useCallback((a, b) => {
    if (a == null || b == null || a === b) return;
    setOrder((prev) => {
      // A tile already sitting in its correct spot (prev[p] === p) is locked
      // in place — never move it, even if it's the swap partner, not just
      // the initiator.
      if (prev[a] === a || prev[b] === b) return prev;
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
  }, []);

  const handleTileClick = useCallback(
    (pos) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      if (solved || order[pos] === pos) return;
      setSelected((sel) => {
        if (sel === null) return pos;
        if (sel === pos) return null;
        swapTiles(sel, pos);
        return null;
      });
    },
    [solved, swapTiles, order]
  );

  const handleKeyDown = useCallback(
    (pos) => (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleTileClick(pos);
      }
    },
    [handleTileClick]
  );

  const handlePointerDown = useCallback(
    (pos) => (e) => {
      if (solved || order[pos] === pos) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      dragStateRef.current = {
        pos,
        startX: e.clientX,
        startY: e.clientY,
        width: rect.width,
        height: rect.height,
        moved: false,
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Pointer capture can fail on some touch browsers; the drag still
        // works via the move/up handlers, just without forced capture.
      }
    },
    [solved, order]
  );

  const findTilePosAt = useCallback((x, y) => {
    const el = document.elementFromPoint(x, y);
    const tileEl = el?.closest?.("[data-jigsaw-pos]");
    if (!tileEl) return null;
    const pos = Number(tileEl.dataset.jigsawPos);
    return Number.isFinite(pos) ? pos : null;
  }, []);

  const handlePointerMove = useCallback(
    (e) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) > 6) {
        drag.moved = true;
        setDraggingPos(drag.pos);
        setSelected(null);
      }
      if (!drag.moved) return;
      setGhost({ x: e.clientX, y: e.clientY, width: drag.width, height: drag.height });
      const overPos = findTilePosAt(e.clientX, e.clientY);
      setDragOverPos(
        overPos != null && overPos !== drag.pos && order[overPos] !== overPos
          ? overPos
          : null
      );
    },
    [order, findTilePosAt]
  );

  const endDrag = useCallback(
    (e) => {
      const drag = dragStateRef.current;
      dragStateRef.current = null;
      if (!drag) return;
      if (drag.moved) {
        suppressClickRef.current = true;
        const overPos = findTilePosAt(e.clientX, e.clientY);
        if (overPos != null) swapTiles(drag.pos, overPos);
      }
      setDraggingPos(null);
      setDragOverPos(null);
      setGhost(null);
    },
    [findTilePosAt, swapTiles]
  );

  const ghostTileIndex = draggingPos != null ? order[draggingPos] : null;
  const ghostCol = ghostTileIndex != null ? ghostTileIndex % size : 0;
  const ghostRow = ghostTileIndex != null ? Math.floor(ghostTileIndex / size) : 0;

  return (
    <>
      <div
        className="room-jigsaw-grid"
        style={{
          gridTemplateColumns: `repeat(${size}, 1fr)`,
          gridTemplateRows: `repeat(${size}, 1fr)`,
          aspectRatio,
        }}
      >
        {order.map((tileIndex, pos) => {
          const col = tileIndex % size;
          const row = Math.floor(tileIndex / size);
          // A tile sitting in its correct spot locks there — no more moving it.
          const locked = tileIndex === pos;
          const isDragging = draggingPos === pos;
          return (
            <div
              key={pos}
              role="button"
              tabIndex={solved || locked ? -1 : 0}
              data-jigsaw-pos={pos}
              className={
                "room-jigsaw-tile" +
                (selected === pos ? " is-selected" : "") +
                (dragOverPos === pos ? " is-dragover" : "") +
                (solved ? " is-solved" : "") +
                (locked ? " is-locked" : "") +
                (isDragging ? " is-dragging" : "")
              }
              onClick={() => handleTileClick(pos)}
              onKeyDown={handleKeyDown(pos)}
              onPointerDown={handlePointerDown(pos)}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              style={{
                touchAction: locked || solved ? "auto" : "none",
                // Hide the artwork in the origin slot while it's being
                // dragged — the floating ghost below carries the piece.
                backgroundImage: isDragging ? "none" : `url(${image})`,
                backgroundSize: `${size * 100}% ${size * 100}%`,
                backgroundPosition: `${(col * 100) / (size - 1)}% ${(row * 100) / (size - 1)}%`,
              }}
              aria-label={`Puzzle tile ${pos + 1}${locked ? " (locked in place)" : ""}`}
            />
          );
        })}
      </div>

      {ghost &&
        draggingPos != null &&
        createPortal(
          <div
            className="room-jigsaw-ghost"
            style={{
              left: ghost.x - ghost.width / 2,
              top: ghost.y - ghost.height / 2,
              width: ghost.width,
              height: ghost.height,
              backgroundImage: `url(${image})`,
              backgroundSize: `${size * 100}% ${size * 100}%`,
              backgroundPosition: `${(ghostCol * 100) / (size - 1)}% ${(ghostRow * 100) / (size - 1)}%`,
            }}
          />,
          document.body
        )}
    </>
  );
}

/** Generic "fill the blanks" modal used for all four text-answer puzzles
 *  in old/room.php (myModal, lastModal, modalCode, audioModal) — each is
 *  the same live-validated-input + submit pattern, just different questions. */
function FillBlankModal({ heading, questions, extraContent, submitLabel = "Submit", onSuccess }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(questions.map((q) => [q.id, ""]))
  );
  const [locked, setLocked] = useState(() =>
    Object.fromEntries(questions.map((q) => [q.id, false]))
  );

  const inputRefs = useRef({});

  // Start typing straight away instead of having to click the first box.
  useEffect(() => {
    inputRefs.current[questions[0]?.id]?.focus();
  }, [questions]);

  /** Moves the cursor to the next still-unsolved box after `id` (wrapping
   *  round to earlier ones), so a correct answer flows into the next blank. */
  const focusNextOpen = useCallback(
    (id) => {
      const start = questions.findIndex((q) => q.id === id);
      for (let step = 1; step < questions.length; step++) {
        const next = questions[(start + step) % questions.length];
        const el = inputRefs.current[next.id];
        if (el && !el.readOnly) {
          el.focus();
          return;
        }
      }
    },
    [questions]
  );

  const handleChange = useCallback(
    (id, answer) => (e) => {
      const val = e.target.value;
      setValues((v) => ({ ...v, [id]: val }));
      if (val.trim().toLowerCase() === answer.toLowerCase()) {
        setLocked((l) => ({ ...l, [id]: true }));
        focusNextOpen(id);
      }
    },
    [focusNextOpen]
  );

  const handleSubmit = useCallback(() => {
    if (questions.some((q) => values[q.id].trim() === "")) {
      void fireGameAlert({ icon: "error", text: "Please insert the answer" });
      return;
    }
    const allCorrect = questions.every(
      (q) => values[q.id].trim().toLowerCase() === q.answer.toLowerCase()
    );
    if (!allCorrect) {
      void fireGameAlert({ icon: "error", text: "Please insert the Correct answer" });
      return;
    }
    onSuccess();
  }, [questions, values, onSuccess]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter" || e.repeat) return;
      e.preventDefault();
      handleSubmit();
    },
    [handleSubmit]
  );

  const solvedCount = questions.filter((q) => locked[q.id]).length;

  return (
    <div className="room-modal-backdrop">
      <div className="room-modal" role="dialog" aria-modal="true">
        <div className="room-modal-header">
          <img src={jewelIconImg} className="room-modal-emblem" alt="" />
          <div className="room-modal-titles">
            <span className="room-modal-eyebrow">Vault puzzle</span>
            {heading && <h4 className="room-modal-heading">{heading}</h4>}
          </div>
          {questions.length > 1 && (
            <span className="room-modal-progress">
              {solvedCount}/{questions.length} solved
            </span>
          )}
        </div>
        {extraContent}
        <div className="room-modal-body">
          {questions.map((q, i) => {
            const val = values[q.id];
            const isLocked = locked[q.id];
            const state = isLocked ? "is-correct" : val ? "is-wrong" : "";
            return (
              <div key={q.id} className={`room-question ${state}`}>
                <div className="room-question-text">
                  {questions.length > 1 && <span className="room-question-num">{i + 1}</span>}
                  <p>{q.label}</p>
                </div>
                <div
                  className={`room-answer-row${questions.length > 1 ? " is-numbered" : ""}`}
                >
                  <div className="room-answer-field">
                    <input
                      ref={(el) => {
                        inputRefs.current[q.id] = el;
                      }}
                      type="text"
                      autoComplete="off"
                      enterKeyHint="done"
                      placeholder="Type your answer"
                      className="room-answer-input"
                      value={val}
                      onChange={handleChange(q.id, q.answer)}
                      onKeyDown={handleKeyDown}
                      readOnly={isLocked}
                    />
                    {isLocked && <span className="room-answer-check" aria-hidden="true">✓</span>}
                  </div>
                  {q.hint && (
                    <span className="room-hint">
                      <span className="room-hint-label">Hint</span>
                      {q.hint}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button type="button" className="room-submit-btn" onClick={handleSubmit}>
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

export default function Game() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const mapWrapRef = useRef(null);
  const isMobilePortrait = useIsMobilePortrait();

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [points, setPoints] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Room escape state machine (mirrors old/room.php's click-gated puzzle chain).
  const [activeModal, setActiveModal] = useState(null); // gibberish | finalCode | jigsaw | trolley | audio | null
  const [leftLockerUnlocked, setLeftLockerUnlocked] = useState(false);
  const [jigsawSolved, setJigsawSolved] = useState(false);
  const [rightLockerUnlocked, setRightLockerUnlocked] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [finished, setFinished] = useState(false);
  const finishedRef = useRef(false);
  const [bulbVisible, setBulbVisible] = useState({ jigsaw: false, audio: false });

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const timeoutFiredRef = useRef(false);
  const lockerClickTimeoutRef = useRef(null);
  const lockerSequenceStartedRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add("game-lock");
    body.classList.remove("common-bg");
    body.style.backgroundImage = "none";
    body.style.backgroundAttachment = "initial";
    return () => {
      html.classList.remove("game-lock");
    };
  }, []);

  useEffect(() => {
    return () => {
      if (lockerClickTimeoutRef.current) {
        window.clearTimeout(lockerClickTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;

    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      // Freeze the clock once the final answer is in (old/room.php's
      // clearInterval(id)), so time spent on the congratulations popup
      // isn't counted.
      if (finishedRef.current) return;
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      // Never count past the 20-minute limit.
      const elapsed = Math.min(ROOM_DURATION_SEC, baseElapsedRef.current + sessionSec);
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = Math.min(ROOM_DURATION_SEC, parseClockToSeconds(st.time));
          const dbTotal = Number(st.total_score) || 0;
          const dbStage1 = Number(st.stage1) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage1);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  // old/room.php's opening toast: "Ravi has found some complex information..."
  useEffect(() => {
    if (!dbReady) return;
    void fireGameAlert({
      icon: "info",
      text: "Ravi has found some complex information at the emergency fire exit.",
    });
  }, [dbReady]);

  const finishStage = useCallback(async () => {
    if (submitting || submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);

    const timeStr = formatSecondsToClock(Math.min(elapsedRef.current, ROOM_DURATION_SEC));
    const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
    const finalTotal = otherStagesTotalRef.current + points;

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: STAGE_NUM,
        score: points,
        time: timeStr,
        total_score: finalTotal,
      });

      // Game is currently the whole game (TOTAL_GAME_STAGES = 1), so this
      // finishStage() call is the one true end-of-adventure point — flag
      // stages.gameover = 1 here (saveGameStageAndReport above never does).
      await markGameOver({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        time: timeStr,
        total_score: finalTotal,
      });
    }

    clearStageActive(STAGE_NUM);
    markStageCompleted(STAGE_NUM, storedUser);

    if (isDemoBypass) {
      void fireGameAlert({
        icon: "info",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        confirmButtonText: "OK",
      }).then(() => {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      });
      return;
    }

    // old/room.php's push() redirected to thankyou.php on both a normal
    // finish and a timeout — this is the one finishStage() exit point that
    // covers both (handleAudioSuccess and the countdown-expiry effect both
    // call it), so no separate timeout-specific redirect is needed.
    navigate("/thankyou");
  }, [submitting, storedUser, isDemoBypass, backendBase, adminToken, stageRowId, points, navigate]);

  // old/room.php's 20-minute countdown; on expiry: "Times up" -> submit -> leave.
  const remainingSec = Math.max(0, ROOM_DURATION_SEC - elapsedSec);
  useEffect(() => {
    if (!dbReady || finished || timeoutFiredRef.current) return;
    if (remainingSec > 0) return;
    timeoutFiredRef.current = true;
    // Freeze the clock at 20:00 while "Times up" is open — otherwise the
    // seconds until OK is clicked were added to the saved time.
    finishedRef.current = true;
    elapsedRef.current = ROOM_DURATION_SEC;
    setElapsedSec(ROOM_DURATION_SEC);
    setFinished(true);
    setActiveModal(null);
    fireGameAlert({ icon: "info", text: "Times up" }).then(() => {
      finishStage();
    });
  }, [remainingSec, dbReady, finished, finishStage]);

  const handleExitClick = useCallback(() => {
    if (finished || leftLockerUnlocked) return;
    setActiveModal("gibberish");
  }, [finished, leftLockerUnlocked]);

  const handleGibberishSuccess = useCallback(() => {
    setPoints((p) => p + 1);

    // Demo players only get a taste — after the first answer, stop the game
    // and send them to the plans page (same notice as inner_peace's finish).
    if (isDemoBypass) {
      finishedRef.current = true;
      setFinished(true);
      setActiveModal(null);
      clearStageActive(STAGE_NUM);
      void fireGameAlert({
        icon: "info",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      });
      return;
    }

    setActiveModal("finalCode");
  }, [isDemoBypass]);

  const handleFinalCodeSuccess = useCallback(() => {
    setPoints((p) => p + 1);
    setActiveModal(null);
    void fireGameAlert({
      icon: "info",
      text:
        "Congratulations. You have made things better for the customer. Open the right locker based on your previous code to proceed.",
    }).then(() => {
      setLeftLockerUnlocked(true);
    });
  }, []);

  const handleLockerClick = useCallback(() => {
    if (!leftLockerUnlocked || jigsawSolved || lockerSequenceStartedRef.current) return;
    lockerSequenceStartedRef.current = true;
    lockerClickTimeoutRef.current = window.setTimeout(() => {
      void fireGameAlert({
        icon: "info",
        text:
          "Yes, locker no 555 is correct. You find shredded pieces of paper. Solve it to proceed.",
      }).then(() => {
        setActiveModal("jigsaw");
      });
    }, 2000);
  }, [leftLockerUnlocked, jigsawSolved]);

  const handleJigsawFinished = useCallback(() => {
    // Hint bulb only appears once the jigsaw is solved, so the finished
    // picture can be revisited later.
    setBulbVisible((b) => ({ ...b, jigsaw: true }));
    void fireGameAlert({
      icon: "success",
      text:
        "Congrats! You have unlocked critical information that is needed to make the customer experience better.",
    });
    setJigsawSolved(true);
  }, []);

  // Old/room.php let players close+continue even before finishing the
  // puzzle; gating it on jigsawSolved here fixes that soft-lock exploit.
  const handleJigsawClose = useCallback(() => {
    if (!jigsawSolved) return;
    setActiveModal(null);
    // Revisiting via the hint bulb — the trolley step is already unlocked.
    if (rightLockerUnlocked) return;
    void fireGameAlert({ icon: "success", text: "Click on the trolley to proceed." }).then(
      () => {
        setRightLockerUnlocked(true);
      }
    );
  }, [jigsawSolved, rightLockerUnlocked]);

  const handleDrawerClick = useCallback(() => {
    if (finished || !rightLockerUnlocked || drawerOpen) return;
    setActiveModal("trolley");
  }, [finished, rightLockerUnlocked, drawerOpen]);

  const handleTrolleySuccess = useCallback(() => {
    setPoints((p) => p + 1);
    setActiveModal(null);
    setDrawerOpen(true);
    void fireGameAlert({
      icon: "info",
      text:
        "Super! Your expertise has helped Ravi in locating the mobile phone, which is the key to exit the vault.",
    }).then(() =>
      fireGameAlert({
        icon: "info",
        text: "Decode this final audio, insert your answer and Ravi will be free! Click on the vault to proceed.",
      })
    ).then(() => {
      setVaultUnlocked(true);
    });
  }, []);

  const handleVaultClick = useCallback(() => {
    if (finished || !vaultUnlocked) return;
    setBulbVisible((b) => ({ ...b, audio: true }));
    setActiveModal("audio");
  }, [finished, vaultUnlocked]);

  // The alert's .then() runs after setPoints has re-rendered, so read
  // finishStage through a ref to get the version that sees the final point.
  const finishStageRef = useRef(finishStage);
  finishStageRef.current = finishStage;

  const handleAudioSuccess = useCallback(() => {
    setPoints((p) => p + 1);
    finishedRef.current = true;
    setFinished(true);
    setActiveModal(null);
    // old/room.php's final swal with img/key.png; only OK moves on to /thankyou.
    void fireGameAlert({
      image: keyImg,
      title: "Congratulations!",
      text:
        "Congratulations! You have shown excellence in living customer-centricity values and helped Ravi escape out of the vault in time! Stay tuned to find out where you stand on the Leaderboard.",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      finishStageRef.current();
    });
  }, []);

  // old/room.php's $(".bulb").eq(0|1).click(): reopen that puzzle's popup.
  const handleBulbClick = useCallback(
    (which) => {
      if (finished || activeModal) return;
      if (bulbVisible[which]) setActiveModal(which);
    },
    [finished, activeModal, bulbVisible]
  );

  // Delegated click handling on the room SVG (same pattern as intro.jsx's map).
  const handleRoomClick = useCallback(
    (e) => {
      if (!mapWrapRef.current) return;

      if (e.target.closest(ROOM_HOTSPOTS.exit)) return handleExitClick();
      if (e.target.closest(ROOM_HOTSPOTS.locker)) return handleLockerClick();
      if (e.target.closest(ROOM_HOTSPOTS.trolley)) return handleDrawerClick();
      if (e.target.closest(ROOM_HOTSPOTS.vault)) return handleVaultClick();
    },
    [handleExitClick, handleLockerClick, handleDrawerClick, handleVaultClick]
  );

  // Imperatively reflect state onto the room SVG (visibility/interactivity),
  // same role jQuery played in old/room.php. mapWrapRef.current is null until
  // dbReady flips true (the room-loading branch renders instead of the SVG),
  // so dbReady must be a dependency here — otherwise this never re-runs once
  // the SVG actually mounts, and hotspots stay uninteractive/uncursored.
  useEffect(() => {
    const root = mapWrapRef.current;
    if (!root) return;

    const bySelector = (sel) => root.querySelector(sel);
    const setInteractive = (el, on) => {
      if (!el) return;
      el.style.pointerEvents = on ? "auto" : "none";
      el.style.cursor = on ? "pointer" : "default";
    };

    setInteractive(bySelector(ROOM_HOTSPOTS.exit), !leftLockerUnlocked);
    setInteractive(bySelector(ROOM_HOTSPOTS.locker), leftLockerUnlocked && !jigsawSolved);
    setInteractive(bySelector(ROOM_HOTSPOTS.vault), vaultUnlocked && !finished);
    setInteractive(
      bySelector(ROOM_HOTSPOTS.trolley),
      rightLockerUnlocked && !drawerOpen
    );
  }, [
    dbReady,
    finished,
    leftLockerUnlocked,
    jigsawSolved,
    rightLockerUnlocked,
    drawerOpen,
    vaultUnlocked,
    isMobilePortrait,
  ]);

  if (!dbReady) {
    return <div className="room-loading">Loading game…</div>;
  }

  return (
    <div className="room-page">
      <div className="room-timer-badge">Time: {formatSecondsToClock(remainingSec)}</div>

      <div className="room-map-wrap" ref={mapWrapRef} onClick={handleRoomClick}>
        {isMobilePortrait ? (
          // "slice" = cover: fill the whole portrait screen (cropping a little
          // top/bottom on taller phones) instead of letterboxing left/right.
          <RoomMapMobile className="room-map-svg" preserveAspectRatio="xMidYMid slice" />
        ) : (
          <RoomMap className="room-map-svg" preserveAspectRatio="xMidYMid meet" />
        )}
      </div>

      {!finished && (bulbVisible.jigsaw || bulbVisible.audio) && (
        <div className="room-bulbs">
          {bulbVisible.jigsaw && (
            <button
              type="button"
              className="room-bulb-btn"
              onClick={() => handleBulbClick("jigsaw")}
              title="View the jigsaw again"
              aria-label="View the jigsaw again"
            >
              <img src={bulbImg} alt="" />
            </button>
          )}
          {bulbVisible.audio && (
            <button
              type="button"
              className="room-bulb-btn"
              onClick={() => handleBulbClick("audio")}
              title="Open the audio clue again"
              aria-label="Open the audio clue again"
            >
              <img src={bulbImg} alt="" />
            </button>
          )}
        </div>
      )}

      {activeModal === "gibberish" && (
        <FillBlankModal
          heading="Cryptic gibberish"
          questions={GIBBERISH_QUESTIONS}
          onSuccess={handleGibberishSuccess}
        />
      )}

      {activeModal === "finalCode" && (
        <FillBlankModal questions={FINAL_CODE_QUESTIONS} onSuccess={handleFinalCodeSuccess} />
      )}

      {activeModal === "trolley" && (
        <FillBlankModal
          heading="Answer the following questions to find the hidden mobile phone"
          questions={TROLLEY_QUESTIONS}
          onSuccess={handleTrolleySuccess}
        />
      )}

      {activeModal === "audio" && (
        <FillBlankModal
          questions={AUDIO_QUESTIONS}
          onSuccess={handleAudioSuccess}
          extraContent={
            <div className="room-audio-extra">
              <img src={mobileImg} alt="" className="room-audio-mobile-img" />
              <audio controls src={audioClip} className="room-audio-player">
                Your browser does not support the audio element.
              </audio>
            </div>
          }
        />
      )}

      {activeModal === "jigsaw" && (
        <div className="room-modal-backdrop">
          <div className="room-modal room-modal--jigsaw">
            <JigsawPuzzle
              image={jisImg}
              size={3}
              aspectRatio={JIS_IMAGE_ASPECT_RATIO}
              startSolved={jigsawSolved}
              onSolved={handleJigsawFinished}
            />
            {!jigsawSolved && (
              <p className="room-jigsaw-hint">Solve the puzzle to continue.</p>
            )}
            <button
              type="button"
              className="room-submit-btn"
              disabled={!jigsawSolved}
              onClick={handleJigsawClose}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
